import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { config } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faStar,
  faMap,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import Map from "../../components/hotels/Map";
import RoomCard from "../../components/hotels/RoomCard";
import SearchForm from "../../components/hotels/SearchForm";

function HotelInformation() {
  const [searchParams] = useSearchParams();
  const [hotelData, setHotelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMobileIndex, setCurrentMobileIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showMap, setShowMap] = useState(false);

  const searchResultId = searchParams.get("searchResultId");

  useEffect(() => {
    const fetchRoomRates = async () => {
      try {
        console.log("Fetching rates for searchResultId:", searchResultId);
        const response = await fetch(
          `${config.backendServer}/api/hotels/rates`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              searchResultId: searchResultId,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          console.error("API Error:", errorData);
          throw new Error(errorData.error || "Failed to fetch room rates");
        }

        const responseData = await response.json();
        console.log("Rates Response:", responseData); // Log the full response

        // Check if the data structure is correct
        if (!responseData?.data?.data?.accommodation) {
          throw new Error("Invalid data structure received from API");
        }

        setHotelData(responseData.data.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching rates:", err);
        setError(err.message || "Failed to load room rates. Please try again.");
        setLoading(false);
      }
    };

    if (searchResultId) {
      fetchRoomRates();
    } else {
      console.error("No searchResultId provided");
      setError("Missing search result ID");
      setLoading(false);
    }
  }, [searchResultId]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const handleMobileNext = () => {
    setCurrentMobileIndex((prevIndex) =>
      prevIndex === hotelData?.accommodation?.photos.length - 1
        ? 0
        : prevIndex + 1
    );
  };

  const handleMobilePrevious = () => {
    setCurrentMobileIndex((prevIndex) =>
      prevIndex === 0
        ? hotelData?.accommodation?.photos.length - 1
        : prevIndex - 1
    );
  };

  if (loading) return <div className="container mx-auto px-4 py-8"></div>;
  if (error)
    return (
      <div className="container mx-auto px-4 py-8 text-red-600">{error}</div>
    );
  if (!hotelData?.accommodation)
    return <div className="container mx-auto px-4 py-8">No data available</div>;

  const { accommodation } = hotelData;

  return (
    <div className="min-h-screen">
      <div className="w-full bg-gray-900">
        <SearchForm />
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Image Gallery */}
        <div className="relative mb-8">
          {/* Mobile Gallery */}
          <div className="md:hidden relative">
            <img
              src={accommodation.photos[currentMobileIndex].url}
              alt={`Hotel view ${currentMobileIndex + 1}`}
              className="w-full h-64 object-cover"
            />
            <button
              onClick={handleMobilePrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              onClick={handleMobileNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs">
              {currentMobileIndex + 1} / {accommodation.photos.length}
            </div>
          </div>

          {/* Desktop Gallery */}
          <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-1 h-[400px]">
            {/* Main large image */}
            <div className="col-span-2 row-span-2 relative">
              <img
                src={accommodation.photos[0].url}
                alt="Main hotel view"
                className="w-full h-full object-cover cursor-pointer hover:opacity-95 transition-opacity"
                onClick={() => openImageModal(accommodation.photos[0].url)}
              />
            </div>
            {/* Smaller images */}
            {accommodation.photos.slice(1, 5).map((photo, index) => (
              <div key={index} className="relative overflow-hidden">
                <img
                  src={photo.url}
                  alt={`Hotel view ${index + 2}`}
                  className="w-full h-full object-cover cursor-pointer hover:opacity-95 transition-opacity"
                  onClick={() => openImageModal(photo.url)}
                />
              </div>
            ))}
            {/* View all photos button */}
            {accommodation.photos.length > 5 && (
              <button
                onClick={() => openImageModal(accommodation.photos[0].url)}
                className="absolute bottom-4 right-4 bg-white text-gray-800 px-4 py-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors text-sm font-medium"
              >
                View all photos ({accommodation.photos.length})
              </button>
            )}
          </div>

          {/* Image Modal */}
          {selectedImage && (
            <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
              <div className="relative w-full max-w-6xl">
                <button
                  onClick={closeImageModal}
                  className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-2xl" />
                </button>

                {/* Left Arrow */}
                <button
                  onClick={() => {
                    const currentIndex = accommodation.photos.findIndex(
                      (photo) => photo.url === selectedImage
                    );
                    const newIndex =
                      currentIndex === 0
                        ? accommodation.photos.length - 1
                        : currentIndex - 1;
                    setSelectedImage(accommodation.photos[newIndex].url);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 p-4 rounded-full"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="text-2xl" />
                </button>

                {/* Right Arrow */}
                <button
                  onClick={() => {
                    const currentIndex = accommodation.photos.findIndex(
                      (photo) => photo.url === selectedImage
                    );
                    const newIndex =
                      currentIndex === accommodation.photos.length - 1
                        ? 0
                        : currentIndex + 1;
                    setSelectedImage(accommodation.photos[newIndex].url);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 p-4 rounded-full"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="text-2xl" />
                </button>

                <img
                  src={selectedImage}
                  alt="Large view"
                  className="w-full h-auto max-h-[90vh] object-contain"
                />
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {accommodation.photos.map((photo, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(photo.url)}
                      className={`w-2 h-2 rounded-full ${
                        selectedImage === photo.url ? "bg-white" : "bg-gray-500"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <div className="border-b mb-6">
          <div className="flex gap-8">
            <button
              onClick={() => scrollToSection("overview")}
              className="py-4 px-2 text-gray-700 hover:text-blue-600 border-b-2 border-transparent hover:border-blue-600 transition-colors"
            >
              Overview
            </button>
            <button
              onClick={() => scrollToSection("about")}
              className="py-4 px-2 text-gray-700 hover:text-blue-600 border-b-2 border-transparent hover:border-blue-600 transition-colors"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection("rooms")}
              className="py-4 px-2 text-gray-700 hover:text-blue-600 border-b-2 border-transparent hover:border-blue-600 transition-colors"
            >
              Rooms
            </button>

            <button
              onClick={() => scrollToSection("policies")}
              className="py-4 px-2 text-gray-700 hover:text-blue-600 border-b-2 border-transparent hover:border-blue-600 transition-colors"
            >
              Policies
            </button>
          </div>
        </div>

        {/* Select Room Button */}
        <button
          onClick={() => scrollToSection("rooms")}
          className={`${config.primaryColorBg} text-black px-6 py-3 rounded-full mb-8 hover:opacity-90 transition-opacity`}
        >
          Select a Room
        </button>

        {/* Overview Section */}
        <div id="overview" className="mb-12">
          <div className="mb-0">
            <h1 className="text-3xl font-bold mb-2">{accommodation.name}</h1>
            <div className="flex items-center gap-2 text-gray-600">
              <span>
                {accommodation.location.address.city_name},{" "}
                {accommodation.location.address.country_code}
              </span>
            </div>
            <div className="flex items-center my-2">
              {[...Array(accommodation.rating)].map((_, i) => (
                <FontAwesomeIcon
                  key={i}
                  icon={faStar}
                  className="text-yellow-400"
                />
              ))}
            </div>
            <div className="bg-blue-500 text-white p-2 rounded-lg w-fit">
              {accommodation.review_score}
            </div>
          </div>
        </div>

        {/* About Section */}
        <div id="about" className="mb-12">
          <h2 className="text-2xl font-semibold mb-4">About this property</h2>
          <div className="flex flex-col md:flex-row gap-8">
            {/* Description and Amenities */}
            <div className="flex-1">
              <div className="mb-4 text-sm">{accommodation.description}</div>

              {/* Check-in Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">
                  Check-in/Check-out
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Check-in</p>
                    <p>
                      {accommodation.check_in_information.check_in_after_time} -{" "}
                      {accommodation.check_in_information.check_in_before_time}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Check-out</p>
                    <p>
                      Before{" "}
                      {accommodation.check_in_information.check_out_before_time}
                    </p>
                  </div>
                </div>
              </div>

              {/* Amenities Section */}
              {accommodation.amenities &&
                accommodation.amenities.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-3">
                      Property Amenities
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {accommodation.amenities.map((amenity, index) => (
                        <div
                          key={index}
                          className="flex items-center p-3 bg-gray-50 rounded-lg"
                        >
                          <span className="text-sm">
                            {amenity.description ||
                              amenity.type.replace(/_/g, " ")}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            {/* Map Preview Section */}
            <div className="md:w-[400px]">
              <div className="w-full overflow-hidden rounded-lg border border-gray-200">
                <button
                  onClick={() => setShowMap(true)}
                  className={`w-full ${config.primaryColorBg} text-black p-3 flex items-center justify-center gap-2 hover:bg-opacity-90 transition-colors`}
                >
                  <FontAwesomeIcon icon={faMap} />
                  Explore the area
                </button>
                <div className="w-full h-32 relative">
                  <img
                    src="/map.webp"
                    alt="Map preview"
                    className="w-full h-full object-cover"
                  />
                  <div
                    className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all cursor-pointer"
                    onClick={() => setShowMap(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map Modal */}
        <Map
          stays={[{ accommodation }]}
          isOpen={showMap}
          onClose={() => setShowMap(false)}
        />

        {/* Rooms Section */}
        <h2 className="text-2xl font-semibold mb-6">Choose your room</h2>
        <div
          id="rooms"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {accommodation.rooms?.map((room, index) => (
            <RoomCard key={index} room={room} />
          ))}
        </div>

        {/* Policies Section */}
        <div id="policies" className="mb-12 flex flex-col gap-2">
          <h2 className="text-2xl font-semibold mb-4">Policies</h2>
          <p className="text-lg font-semibold">Check In</p>
          {accommodation.check_in_information && (
            <>
              <p>
                Check in after{" "}
                {accommodation.check_in_information.check_in_after_time}
              </p>
              <p>
                Check out before{" "}
                {accommodation.check_in_information.check_out_before_time}
              </p>
            </>
          )}

          <p className="text-lg mt-4 font-semibold">Important Information</p>
          {accommodation.rooms?.[0]?.rates?.[0]?.conditions && (
            <>
              {accommodation.rooms[0].rates[0].conditions.find(
                (c) => c.title === "Photo Policy"
              )?.description && (
                <>
                  <p className="text-lg mt-4 font-semibold">Photo Policy</p>
                  <p>
                    {
                      accommodation.rooms[0].rates[0].conditions.find(
                        (c) => c.title === "Photo Policy"
                      ).description
                    }
                  </p>
                </>
              )}

              {accommodation.rooms[0].rates[0].conditions.find(
                (c) => c.title === "Hotel Pet Policy"
              )?.description && (
                <>
                  <p className="text-lg mt-4 font-semibold">Hotel Pet Policy</p>
                  <p>
                    {
                      accommodation.rooms[0].rates[0].conditions.find(
                        (c) => c.title === "Hotel Pet Policy"
                      ).description
                    }
                  </p>
                </>
              )}

              {accommodation.rooms[0].rates[0].conditions.find(
                (c) => c.title === "Important Information"
              )?.description && (
                <>
                  <p className="text-lg mt-4 font-semibold">
                    Additional Important Information
                  </p>
                  <p>
                    {
                      accommodation.rooms[0].rates[0].conditions.find(
                        (c) => c.title === "Important Information"
                      ).description
                    }
                  </p>
                </>
              )}
            </>
          )}

          {accommodation.key_collection && (
            <div>
              <p className="text-lg font-semibold">Access Methods</p>
              <p>Key collection:{accommodation.key_collection}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HotelInformation;
